import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * Button — shadcn idiom (CVA + Radix Slot), themed onto HofSync brand tokens.
 * Proves: Tailwind+CVA compile in the DS, brand parity via `var(--color-*)`,
 * and the same DS public-API export surface (`asChild`, variants).
 */
const buttonVariants = cva(
  // border-0 neutralises the native <button> UA border (we exclude Tailwind's
  // preflight, so it isn't reset globally); the outline variant re-adds a border.
  // font-sans resets the UA font-family — <button> doesn't inherit the document
  // font (#1927). Under asChild/Slot the class lands on the child, where it is
  // harmless (matches the inherited Inter anyway).
  'tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:whitespace-nowrap tw:rounded-md tw:border-0 tw:font-sans tw:text-sm tw:font-medium tw:transition-colors tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:disabled:pointer-events-none tw:disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'tw:bg-primary tw:text-primary-foreground tw:hover:bg-primary-hover',
        secondary:
          'tw:bg-secondary tw:text-secondary-foreground tw:hover:bg-accent',
        outline:
          'tw:border tw:border-border tw:bg-transparent tw:text-foreground tw:hover:bg-accent',
        ghost: 'tw:bg-transparent tw:text-foreground tw:hover:bg-accent',
        destructive:
          'tw:bg-destructive tw:text-destructive-foreground tw:hover:bg-destructive-hover',
      },
      size: {
        default: 'tw:h-9 tw:px-4 tw:py-2',
        sm: 'tw:h-8 tw:rounded-md tw:px-3 tw:text-xs',
        lg: 'tw:h-10 tw:rounded-md tw:px-6',
        icon: 'tw:h-9 tw:w-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { buttonVariants };
